// Firebase services for managing patients, owners, medical records, etc.
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  getDoc,
  setDoc,
  getFirestore
} from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import app from '../config/firebase';

// Inicializar servicios de Firebase directamente aquí
const db = getFirestore(app);
const auth = getAuth(app);

// AUTENTICACIÓN (Auth) Services
export const authService = {
  // Login anónimo para desarrollo
  async loginAnonymously() {
    try {
      console.log('🔐 AUTH SERVICE: Intentando login anónimo...');
      const result = await signInAnonymously(auth);
      console.log('✅ AUTH SERVICE: Login anónimo exitoso:', result.user.uid);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('❌ AUTH SERVICE: Error en login anónimo:', error);
      return { success: false, error: error.message };
    }
  },

  // Verificar estado de autenticación
  getCurrentUser() {
    return auth.currentUser;
  },

  // Esperar a que se complete la autenticación
  async waitForAuth() {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }
};

// GALERÍA PÚBLICA: copia mínima (sin datos del propietario) de los pacientes
// activos con foto, que es lo único que la web pública puede leer.
const syncPublicGalleryEntry = async (patientId) => {
  try {
    const snap = await getDoc(doc(db, 'pacientes', patientId));
    const ref = doc(db, 'galeriaPublica', patientId);
    const p = snap.exists() ? snap.data() : null;
    if (p && p.activo !== false && p.estado === 'activo' && p.foto && p.foto.trim() !== '') {
      await setDoc(ref, {
        nombre: p.nombre || '',
        especie: p.especie || '',
        raza: p.raza || '',
        foto: p.foto,
        observaciones: p.observaciones || ''
      });
    } else {
      await deleteDoc(ref);
    }
  } catch (error) {
    // No debe romper la operación principal sobre el paciente
    console.error('Error sincronizando galería pública:', error);
  }
};

// PACIENTES (Patients) Services
export const patientsService = {
  // Crear nuevo paciente
  async createPatient(patientData) {
    try {
      const docRef = await addDoc(collection(db, 'pacientes'), {
        ...patientData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        activo: true
      });
      await syncPublicGalleryEntry(docRef.id);
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('Error creando paciente:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener todos los pacientes activos
  async getAllPatients() {
    try {
      console.log('🔍 PATIENTS SERVICE: Iniciando carga de pacientes...');
      
      // Verificar autenticación (solo informativo, no bloquear)
      const currentUser = authService.getCurrentUser();
      console.log('🔐 PATIENTS SERVICE: Usuario actual:', currentUser?.uid || 'No autenticado');
      
      console.log('� PATIENTS SERVICE: Ejecutando query de pacientes (sin autenticación)...');
      
      // Intentar query simple primero (sin orderBy para evitar problemas de índices)
      const querySnapshot = await getDocs(collection(db, 'pacientes'));
      const patients = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Filtrar solo pacientes activos
        if (data.activo !== false) {
          console.log('👥 Paciente activo encontrado:', doc.id, data);
          patients.push({ id: doc.id, ...data });
        }
      });
      
      // Ordenar manualmente por fecha de creación (más recientes primero)
      patients.sort((a, b) => {
        const fechaA = a.fechaCreacion?.toDate?.() || new Date(0);
        const fechaB = b.fechaCreacion?.toDate?.() || new Date(0);
        return fechaB - fechaA;
      });
      
      console.log('✅ PATIENTS SERVICE: Total pacientes activos cargados:', patients.length);
      return { success: true, data: patients };
      
    } catch (error) {
      console.error('❌ PATIENTS SERVICE: Error obteniendo pacientes:', error);
      console.error('❌ Detalles del error:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      return { 
        success: false, 
        error: `Error de conexión: ${error.message}. Verifica las reglas de Firebase Firestore y que el proyecto esté configurado correctamente.` 
      };
    }
  },

  // Obtener paciente por ID
  async getPatientById(patientId) {
    try {
      const docRef = doc(db, 'pacientes', patientId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
      } else {
        return { success: false, error: 'Paciente no encontrado' };
      }
    } catch (error) {
      console.error('Error obteniendo paciente:', error);
      return { success: false, error: error.message };
    }
  },

  // Buscar pacientes por propietario
  async getPatientsByOwner(ownerName) {
    try {
      const q = query(
        collection(db, 'pacientes'),
        where('propietario', '==', ownerName),
        where('activo', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const patients = [];
      querySnapshot.forEach((doc) => {
        patients.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: patients };
    } catch (error) {
      console.error('Error buscando pacientes por propietario:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar paciente
  async updatePatient(patientId, updatedData) {
    try {
      const patientRef = doc(db, 'pacientes', patientId);
      await updateDoc(patientRef, {
        ...updatedData,
        fechaActualizacion: serverTimestamp()
      });
      await syncPublicGalleryEntry(patientId);
      return { success: true };
    } catch (error) {
      console.error('Error actualizando paciente:', error);
      return { success: false, error: error.message };
    }
  },

  // Eliminar paciente (soft delete)
  async deletePatient(patientId) {
    try {
      const patientRef = doc(db, 'pacientes', patientId);
      await updateDoc(patientRef, {
        activo: false,
        fechaEliminacion: serverTimestamp()
      });
      await syncPublicGalleryEntry(patientId);
      return { success: true };
    } catch (error) {
      console.error('Error eliminando paciente:', error);
      return { success: false, error: error.message };
    }
  },

  // Galería pública (lectura sin login): solo la colección galeriaPublica
  async getPublicGallery() {
    try {
      const querySnapshot = await getDocs(collection(db, 'galeriaPublica'));
      const data = [];
      querySnapshot.forEach((d) => data.push({ id: d.id, ...d.data() }));
      return { success: true, data };
    } catch (error) {
      console.error('Error obteniendo galería pública:', error);
      return { success: false, error: error.message };
    }
  },

  // Reconstruye galeriaPublica desde pacientes (idempotente, solo con sesión de admin).
  // No modifica ni borra pacientes.
  async rebuildPublicGallery() {
    try {
      const snapshot = await getDocs(collection(db, 'pacientes'));
      await Promise.all(snapshot.docs.map((d) => syncPublicGalleryEntry(d.id)));
      return { success: true, total: snapshot.size };
    } catch (error) {
      console.error('Error reconstruyendo galería pública:', error);
      return { success: false, error: error.message };
    }
  }
};

// HISTORIAS CLÍNICAS (Medical Records) Services
export const medicalRecordsService = {
  // Crear nueva historia clínica
  async createMedicalRecord(recordData) {
    try {
      const docRef = await addDoc(collection(db, 'historias_clinicas'), {
        ...recordData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp()
      });
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('Error creando historia clínica:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener historias clínicas por paciente
  async getMedicalRecordsByPatient(patientId) {
    try {
      const q = query(
        collection(db, 'historias_clinicas'),
        where('pacienteId', '==', patientId),
        orderBy('fechaCreacion', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const records = [];
      querySnapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: records };
    } catch (error) {
      console.error('Error obteniendo historias clínicas:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar historia clínica
  async updateMedicalRecord(recordId, updatedData) {
    try {
      const recordRef = doc(db, 'historias_clinicas', recordId);
      await updateDoc(recordRef, {
        ...updatedData,
        fechaActualizacion: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error actualizando historia clínica:', error);
      return { success: false, error: error.message };
    }
  }
};

// RECETAS (Prescriptions) Services
export const prescriptionsService = {
  // Crear nueva receta
  async createPrescription(prescriptionData) {
    try {
      const docRef = await addDoc(collection(db, 'recetas'), {
        ...prescriptionData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        estado: 'activa'
      });
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('Error creando receta:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener recetas por paciente
  async getPrescriptionsByPatient(patientId) {
    try {
      // Intentar primero con where sin orderBy (no requiere índice)
      console.log('🔍 Intentando obtener recetas por paciente ID:', patientId);
      try {
        const q = query(
          collection(db, 'recetas'),
          where('pacienteId', '==', patientId)
        );
        const querySnapshot = await getDocs(q);
        const prescriptions = [];
        querySnapshot.forEach((doc) => {
          prescriptions.push({ id: doc.id, ...doc.data() });
        });
        
        // Ordenar manualmente en memoria
        prescriptions.sort((a, b) => {
          const fechaA = a.fechaCreacion?.toDate?.() || new Date(0);
          const fechaB = b.fechaCreacion?.toDate?.() || new Date(0);
          return fechaB - fechaA;
        });
        
        console.log('✅ Recetas obtenidas por ID:', prescriptions.length);
        return { success: true, data: prescriptions };
      } catch (error) {
        // Si falla por índice u otro error, intentar obtener todas y filtrar
        console.warn('⚠️ Error con where, intentando obtener todas las recetas:', error.message);
        const allPresResult = await this.getAllPrescriptions();
        if (allPresResult.success && Array.isArray(allPresResult.data)) {
          const filtered = allPresResult.data.filter(r => r.pacienteId === patientId);
          console.log('✅ Recetas obtenidas filtrando todas:', filtered.length);
          return { success: true, data: filtered };
        }
        return { success: false, data: [], error: error.message };
      }
    } catch (error) {
      console.error('Error obteniendo recetas:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener todas las recetas
  async getAllPrescriptions() {
    try {
      const q = query(
        collection(db, 'recetas'),
        orderBy('fechaCreacion', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const prescriptions = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        prescriptions.push({ 
          id: doc.id, 
          ...data,
          // Convertir timestamp a fecha legible
          fecha: data.fechaCreacion?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString()
        });
      });
      return { success: true, data: prescriptions };
    } catch (error) {
      console.error('Error obteniendo todas las recetas:', error);
      return { success: false, error: error.message };
    }
  },

  // Método alias para compatibilidad
  async getAll() {
    try {
      const result = await this.getAllPrescriptions();
      return result.success ? result.data : [];
    } catch (error) {
      console.error('Error en getAll:', error);
      return [];
    }
  },

  // Actualizar estado de receta
  async updatePrescriptionStatus(prescriptionId, status) {
    try {
      const prescriptionRef = doc(db, 'recetas', prescriptionId);
      await updateDoc(prescriptionRef, {
        estado: status,
        fechaActualizacion: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error actualizando estado de receta:', error);
      return { success: false, error: error.message };
    }
  }
};

// INVENTARIO (Inventory) Services
export const inventoryService = {
  // Crear nuevo item de inventario
  async createInventoryItem(itemData) {
    try {
      const docRef = await addDoc(collection(db, 'inventario'), {
        ...itemData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        activo: true
      });
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('Error creando item de inventario:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener todo el inventario
  async getAllInventoryItems() {
    try {
      // Consulta simplificada sin índice compuesto
      const querySnapshot = await getDocs(collection(db, 'inventario'));
      const items = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Filtrar manualmente en lugar de usar where en la consulta
        if (data.activo !== false) {
          items.push({ id: doc.id, ...data });
        }
      });
      
      // Ordenar manualmente por nombre
      items.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      
      return { success: true, data: items };
    } catch (error) {
      console.error('Error obteniendo inventario:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar cantidad de inventario
  async updateInventoryQuantity(itemId, newQuantity) {
    try {
      const itemRef = doc(db, 'inventario', itemId);
      await updateDoc(itemRef, {
        cantidad: newQuantity,
        fechaActualizacion: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error actualizando cantidad de inventario:', error);
      return { success: false, error: error.message };
    }
  }
};

// CITAS (Appointments) Services
export const appointmentsService = {
  // Crear nueva cita
  async createAppointment(appointmentData) {
    try {
      const docRef = await addDoc(collection(db, 'citas'), {
        ...appointmentData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        estado: 'programada'
      });
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('Error creando cita:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener citas por fecha
  async getAppointmentsByDate(date) {
    try {
      const q = query(
        collection(db, 'citas'),
        where('fecha', '==', date),
        orderBy('hora', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const appointments = [];
      querySnapshot.forEach((doc) => {
        appointments.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: appointments };
    } catch (error) {
      console.error('Error obteniendo citas por fecha:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar estado de cita
  async updateAppointmentStatus(appointmentId, status) {
    try {
      const appointmentRef = doc(db, 'citas', appointmentId);
      await updateDoc(appointmentRef, {
        estado: status,
        fechaActualizacion: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error actualizando estado de cita:', error);
      return { success: false, error: error.message };
    }
  }
};

// CONSENTIMIENTOS (Consents) Services
export const consentsService = {
  // Crear nuevo consentimiento
  async createConsent(consentData) {
    try {
      const docRef = await addDoc(collection(db, 'consentimientos'), {
        ...consentData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        estado: 'firmado'
      });
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('Error creando consentimiento:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener consentimientos por paciente
  async getConsentsByPatient(patientId) {
    try {
      const q = query(
        collection(db, 'consentimientos'),
        where('pacienteId', '==', patientId),
        orderBy('fechaCreacion', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const consents = [];
      querySnapshot.forEach((doc) => {
        consents.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, data: consents };
    } catch (error) {
      console.error('Error obteniendo consentimientos:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar estado de consentimiento
  async updateConsentStatus(consentId, status) {
    try {
      const consentRef = doc(db, 'consentimientos', consentId);
      await updateDoc(consentRef, {
        estado: status,
        fechaActualizacion: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error actualizando estado de consentimiento:', error);
      return { success: false, error: error.message };
    }
  }
};

// HISTORIAS CLÍNICAS (Medical Records) Services
export const historiaClinicaService = {
  // Crear nueva historia clínica
  async createHistoriaClinica(historiaData) {
    try {
      const docRef = await addDoc(collection(db, 'historiasClinicas'), {
        ...historiaData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        activo: true
      });
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('Error creando historia clínica:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener historias clínicas por paciente
  async getHistoriasByPaciente(pacienteId) {
    try {
      console.log('🔍 FIREBASE SERVICE: Iniciando búsqueda de historias');
      console.log('📋 Paciente ID recibido:', pacienteId);
      console.log('📋 Tipo de ID:', typeof pacienteId);
      console.log('📋 ID es válido:', !!pacienteId);
      
      if (!pacienteId) {
        console.error('❌ Error: pacienteId es null o undefined');
        return { success: false, error: 'ID de paciente no válido' };
      }

      // Primero, vamos a obtener TODAS las historias para debug (SIN orderBy)
      console.log('🔍 Obteniendo TODAS las historias clínicas para debug...');
      const allQuery = query(
        collection(db, 'historiasClinicas')
        // Sin orderBy para evitar problema de índice
      );
      
      const allSnapshot = await getDocs(allQuery);
      console.log('📊 Total de historias en la base de datos:', allSnapshot.size);
      
      if (allSnapshot.size > 0) {
        console.log('📋 Todas las historias encontradas:');
        const allHistorias = [];
        allSnapshot.forEach((doc) => {
          const data = doc.data();
          allHistorias.push({
            id: doc.id,
            pacienteId: data.pacienteId,
            motivoConsulta: data.motivoConsulta,
            fecha: data.fecha || data.fechaCreacion?.toDate?.()?.toISOString?.()?.split('T')[0]
          });
        });
        
        // Ordenar manualmente y mostrar
        allHistorias.sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));
        allHistorias.forEach((h) => {
          console.log(`   - ID: ${h.id}, pacienteId: "${h.pacienteId}", motivo: "${h.motivoConsulta}"`);
        });
      }
      
      // Ahora buscar específicamente para este paciente (SIN orderBy para evitar índice)
      console.log('🎯 Buscando historias específicas para paciente:', pacienteId);
      const q = query(
        collection(db, 'historiasClinicas'),
        where('pacienteId', '==', pacienteId)
        // Temporalmente sin orderBy para evitar error de índice
      );
      
      const querySnapshot = await getDocs(q);
      console.log('📊 Historias encontradas para este paciente:', querySnapshot.size);
      
      const historias = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('📄 Documento encontrado:', {
          id: doc.id,
          pacienteId: data.pacienteId,
          motivoConsulta: data.motivoConsulta,
          fecha: data.fecha,
          activo: data.activo
        });
        
        // Solo incluir si no está explícitamente marcado como inactivo
        if (data.activo !== false) {
          const historia = { 
            id: doc.id, 
            ...data,
            // Convertir Timestamp a string para el frontend
            fecha: data.fechaCreacion?.toDate?.()?.toISOString?.()?.split('T')[0] || data.fecha
          };
          console.log('✅ Historia incluida en resultado:', historia.id);
          historias.push(historia);
        } else {
          console.log('⚠️ Historia excluida (activo=false):', doc.id);
        }
      });
      
      // Ordenar manualmente por fecha (más reciente primero)
      historias.sort((a, b) => {
        const fechaA = new Date(a.fecha || a.fechaCreacion || 0);
        const fechaB = new Date(b.fecha || b.fechaCreacion || 0);
        return fechaB - fechaA;
      });
      
      console.log('📋 Historias finales a retornar:', historias.length);
      console.log('📋 Datos finales:', historias);
      
      return { success: true, data: historias };
    } catch (error) {
      console.error('❌ Error en getHistoriasByPaciente:', error);
      console.error('❌ Error completo:', error.message);
      console.error('❌ Stack trace:', error.stack);
      return { success: false, error: error.message };
    }
  },

  // Obtener TODAS las historias clínicas (una sola consulta, para uso del asistente)
  async getAllHistoriasClinicas() {
    try {
      const querySnapshot = await getDocs(collection(db, 'historiasClinicas'));
      const historias = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.activo !== false) {
          historias.push({
            id: doc.id,
            ...data,
            fecha: data.fechaCreacion?.toDate?.()?.toISOString?.()?.split('T')[0] || data.fecha
          });
        }
      });
      return { success: true, data: historias };
    } catch (error) {
      console.error('❌ Error obteniendo todas las historias clínicas:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  // Obtener historia clínica específica
  async getHistoriaById(historiaId) {
    try {
      const docRef = doc(db, 'historiasClinicas', historiaId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          success: true, 
          data: { 
            id: docSnap.id, 
            ...data,
            fecha: data.fechaCreacion?.toDate?.()?.toISOString?.()?.split('T')[0] || data.fecha
          } 
        };
      } else {
        return { success: false, error: 'Historia clínica no encontrada' };
      }
    } catch (error) {
      console.error('Error obteniendo historia clínica:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar historia clínica
  async updateHistoriaClinica(historiaId, historiaData) {
    try {
      const historiaRef = doc(db, 'historiasClinicas', historiaId);
      await updateDoc(historiaRef, {
        ...historiaData,
        fechaActualizacion: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error actualizando historia clínica:', error);
      return { success: false, error: error.message };
    }
  },

  // Eliminar historia clínica (soft delete)
  async deleteHistoriaClinica(historiaId) {
    try {
      const historiaRef = doc(db, 'historiasClinicas', historiaId);
      await updateDoc(historiaRef, {
        activo: false,
        fechaEliminacion: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error eliminando historia clínica:', error);
      return { success: false, error: error.message };
    }
  },

  // Buscar historias clínicas por motivo de consulta
  async searchHistoriasByMotivo(searchTerm) {
    try {
      const q = query(
        collection(db, 'historiasClinicas'),
        where('activo', '!=', false),
        orderBy('fechaCreacion', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const historias = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.activo !== false && 
            data.motivoConsulta?.toLowerCase().includes(searchTerm.toLowerCase())) {
          historias.push({ 
            id: doc.id, 
            ...data,
            fecha: data.fechaCreacion?.toDate?.()?.toISOString?.()?.split('T')[0] || data.fecha
          });
        }
      });
      return { success: true, data: historias };
    } catch (error) {
      console.error('Error buscando historias clínicas:', error);
      return { success: false, error: error.message };
    }
  }
};

// RECETARIOS (Prescriptions) Services
export const recetariosService = {
  // Crear nueva receta
  async createReceta(recetaData) {
    try {
      console.log('💊 RECETA SERVICE: Creando nueva receta...');
      console.log('💊 Datos de entrada:', recetaData);
      
      const docRef = await addDoc(collection(db, 'recetas'), {
        ...recetaData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        estado: recetaData.estado || 'activa',
        activo: true
      });
      
      console.log('✅ RECETA SERVICE: Receta creada con ID:', docRef.id);
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error creando receta:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener todas las recetas
  async getAllRecetas() {
    try {
      console.log('💊 RECETA SERVICE: Cargando todas las recetas...');
      
      // Query simple sin autenticación requerida
      const querySnapshot = await getDocs(collection(db, 'recetas'));
      const recetas = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('📄 Receta encontrada:', doc.id, data);
        
        // Solo incluir recetas activas
        if (data.activo !== false) {
          recetas.push({
            id: doc.id,
            ...data,
            fecha: data.fechaCreacion?.toDate?.()?.toLocaleDateString() || data.fecha || new Date().toLocaleDateString(),
            fechaCompleta: data.fechaCreacion?.toDate?.() || new Date(data.fecha) || new Date()
          });
        }
      });
      
      // Ordenar manualmente por fecha (más recientes primero)
      recetas.sort((a, b) => {
        const fechaA = a.fechaCompleta || new Date(0);
        const fechaB = b.fechaCompleta || new Date(0);
        return fechaB - fechaA;
      });
      
      console.log('✅ RECETA SERVICE: Total recetas cargadas:', recetas.length);
      return { success: true, data: recetas };
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error cargando recetas:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener recetas por paciente
  async getRecetasByPaciente(pacienteId) {
    try {
      console.log('💊 RECETA SERVICE: Buscando recetas para paciente:', pacienteId);
      console.log('📊 Tipo de pacienteId:', typeof pacienteId);
      
      if (!pacienteId) {
        console.error('❌ RECETA SERVICE: pacienteId no válido:', pacienteId);
        return { success: false, error: 'ID de paciente no válido' };
      }

      // Consulta filtrada directa por pacienteId (sin escanear toda la colección)
      console.log('🔍 Buscando recetas por pacienteId:', pacienteId);
      try {
        const q1 = query(
          collection(db, 'recetas'),
          where('pacienteId', '==', pacienteId)
        );
        
        const snapshot1 = await getDocs(q1);
        console.log('📊 Recetas encontradas con query simple:', snapshot1.size);
        
        const recetas = [];
        
        snapshot1.forEach((doc) => {
          const data = doc.data();
          console.log('📄 Receta encontrada:', doc.id, data);
          
          // Solo incluir si está activa o no tiene el campo activo
          if (data.activo !== false) {
            recetas.push({
              id: doc.id,
              ...data,
              fecha: data.fechaCreacion?.toDate?.()?.toLocaleDateString() || data.fecha || new Date().toLocaleDateString(),
              fechaCompleta: data.fechaCreacion?.toDate?.() || new Date(data.fecha) || new Date()
            });
          } else {
            console.log('⚠️ Receta excluida porque activo=false:', doc.id);
          }
        });
        
        // Ordenar manualmente por fecha (más recientes primero)
        recetas.sort((a, b) => {
          const fechaA = a.fechaCompleta || new Date(0);
          const fechaB = b.fechaCompleta || new Date(0);
          return fechaB - fechaA;
        });
        
        console.log('✅ RECETA SERVICE: Recetas del paciente encontradas:', recetas.length);
        return { success: true, data: recetas };
      } catch (queryError) {
        console.error('❌ Error con la primera consulta:', queryError);
        // Intentemos con una estrategia alternativa
        
        // Obtener todas y filtrar manualmente
        const allSnapshot = await getDocs(collection(db, 'recetas'));
        const recetas = [];
        
        allSnapshot.forEach((doc) => {
          const data = doc.data();
          // Comparar pacienteId como string por si acaso
          if (data.pacienteId === pacienteId || data.pacienteId === String(pacienteId)) {
            if (data.activo !== false) {
              recetas.push({
                id: doc.id,
                ...data,
                fecha: data.fechaCreacion?.toDate?.()?.toLocaleDateString() || data.fecha || new Date().toLocaleDateString(),
                fechaCompleta: data.fechaCreacion?.toDate?.() || new Date(data.fecha) || new Date()
              });
            }
          }
        });
        
        // Ordenar manualmente por fecha (más recientes primero)
        recetas.sort((a, b) => {
          const fechaA = a.fechaCompleta || new Date(0);
          const fechaB = b.fechaCompleta || new Date(0);
          return fechaB - fechaA;
        });
        
        console.log('✅ RECETA SERVICE (método alternativo): Recetas encontradas:', recetas.length);
        return { success: true, data: recetas };
      }
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error buscando recetas del paciente:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener receta por ID
  async getRecetaById(recetaId) {
    try {
      console.log('💊 RECETA SERVICE: Obteniendo receta por ID:', recetaId);
      
      const docRef = doc(db, 'recetas', recetaId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('✅ RECETA SERVICE: Receta encontrada:', data);
        
        return { 
          success: true, 
          data: { 
            id: docSnap.id, 
            ...data,
            fecha: data.fechaCreacion?.toDate?.()?.toLocaleDateString() || data.fecha || new Date().toLocaleDateString()
          } 
        };
      } else {
        console.log('❌ RECETA SERVICE: Receta no encontrada');
        return { success: false, error: 'Receta no encontrada' };
      }
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error obteniendo receta:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar receta
  async updateReceta(recetaId, updateData) {
    try {
      console.log('💊 RECETA SERVICE: Actualizando receta:', recetaId);
      console.log('💊 Datos de actualización:', updateData);
      
      const docRef = doc(db, 'recetas', recetaId);
      await updateDoc(docRef, {
        ...updateData,
        fechaActualizacion: serverTimestamp()
      });
      
      console.log('✅ RECETA SERVICE: Receta actualizada exitosamente');
      return { success: true };
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error actualizando receta:', error);
      return { success: false, error: error.message };
    }
  },

  // Eliminar receta (soft delete)
  async deleteReceta(recetaId) {
    try {
      console.log('💊 RECETA SERVICE: Eliminando receta:', recetaId);
      
      const docRef = doc(db, 'recetas', recetaId);
      await updateDoc(docRef, {
        activo: false,
        fechaEliminacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp()
      });
      
      console.log('✅ RECETA SERVICE: Receta eliminada exitosamente (soft delete)');
      return { success: true };
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error eliminando receta:', error);
      return { success: false, error: error.message };
    }
  },

  // Eliminar receta permanentemente
  async deleteRecetaPermanently(recetaId) {
    try {
      console.log('💊 RECETA SERVICE: Eliminando receta permanentemente:', recetaId);
      
      const docRef = doc(db, 'recetas', recetaId);
      await deleteDoc(docRef);
      
      console.log('✅ RECETA SERVICE: Receta eliminada permanentemente');
      return { success: true };
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error eliminando receta permanentemente:', error);
      return { success: false, error: error.message };
    }
  },

  // Cambiar estado de receta
  async updateEstadoReceta(recetaId, nuevoEstado) {
    try {
      console.log('💊 RECETA SERVICE: Cambiando estado de receta:', recetaId, 'a', nuevoEstado);
      
      const docRef = doc(db, 'recetas', recetaId);
      await updateDoc(docRef, {
        estado: nuevoEstado,
        fechaActualizacion: serverTimestamp()
      });
      
      console.log('✅ RECETA SERVICE: Estado de receta actualizado');
      return { success: true };
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error actualizando estado:', error);
      return { success: false, error: error.message };
    }
  },

  // Buscar recetas por múltiples criterios
  async searchRecetas(criteria = {}) {
    try {
      console.log('💊 RECETA SERVICE: Buscando recetas con criterios:', criteria);
      
      let q = collection(db, 'recetas');
      
      // Si hay criterios específicos, aplicar filtros
      if (criteria.pacienteId) {
        q = query(q, where('pacienteId', '==', criteria.pacienteId));
      }
      
      if (criteria.veterinario) {
        q = query(q, where('veterinario', '==', criteria.veterinario));
      }
      
      if (criteria.estado) {
        q = query(q, where('estado', '==', criteria.estado));
      }
      
      const querySnapshot = await getDocs(q);
      const recetas = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Filtros adicionales en el cliente
        let incluir = true;
        
        if (criteria.fechaInicio) {
          const fechaReceta = data.fechaCreacion?.toDate?.() || new Date(data.fecha);
          const fechaInicio = new Date(criteria.fechaInicio);
          if (fechaReceta < fechaInicio) incluir = false;
        }
        
        if (criteria.fechaFin) {
          const fechaReceta = data.fechaCreacion?.toDate?.() || new Date(data.fecha);
          const fechaFin = new Date(criteria.fechaFin);
          if (fechaReceta > fechaFin) incluir = false;
        }
        
        if (criteria.medicamento) {
          const tieneMedicamento = data.medicamentos?.some(med => 
            med.medicamento?.toLowerCase().includes(criteria.medicamento.toLowerCase())
          );
          if (!tieneMedicamento) incluir = false;
        }
        
        if (incluir && data.activo !== false) {
          recetas.push({
            id: doc.id,
            ...data,
            fecha: data.fechaCreacion?.toDate?.()?.toLocaleDateString() || data.fecha || new Date().toLocaleDateString(),
            fechaCompleta: data.fechaCreacion?.toDate?.() || new Date(data.fecha) || new Date()
          });
        }
      });
      
      // Ordenar por fecha
      recetas.sort((a, b) => (b.fechaCompleta || new Date(0)) - (a.fechaCompleta || new Date(0)));
      
      console.log('✅ RECETA SERVICE: Recetas encontradas:', recetas.length);
      return { success: true, data: recetas };
    } catch (error) {
      console.error('❌ RECETA SERVICE: Error en búsqueda:', error);
      return { success: false, error: error.message };
    }
  }
};

export const remisionesService = {
  // Crear nueva remisión clínica
  async createRemision(remisionData) {
    try {
      console.log('🏥 REMISION SERVICE: Creando nueva remisión...');
      const docRef = await addDoc(collection(db, 'remisiones'), {
        ...remisionData,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
        estado: remisionData.estado || 'activa',
        activo: true
      });

      console.log('✅ REMISION SERVICE: Remisión creada con ID:', docRef.id);
      return { id: docRef.id, success: true };
    } catch (error) {
      console.error('❌ REMISION SERVICE: Error creando remisión:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener remisiones por paciente
  async getRemisionesByPaciente(pacienteId) {
    try {
      console.log('🏥 REMISION SERVICE: Buscando remisiones para paciente:', pacienteId);

      if (!pacienteId) {
        console.error('❌ REMISION SERVICE: pacienteId no válido:', pacienteId);
        return { success: false, error: 'ID de paciente no válido' };
      }

      const q = query(
        collection(db, 'remisiones'),
        where('pacienteId', '==', pacienteId)
      );

      const snapshot = await getDocs(q);
      const remisiones = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.activo !== false) {
          remisiones.push({
            id: doc.id,
            ...data,
            fecha: data.fechaCreacion?.toDate?.()?.toLocaleDateString() || data.fecha || new Date().toLocaleDateString(),
            fechaCompleta: data.fechaCreacion?.toDate?.() || new Date(data.fecha) || new Date()
          });
        }
      });

      // Ordenar manualmente por fecha (más recientes primero)
      remisiones.sort((a, b) => {
        const fechaA = a.fechaCompleta || new Date(0);
        const fechaB = b.fechaCompleta || new Date(0);
        return fechaB - fechaA;
      });

      console.log('✅ REMISION SERVICE: Remisiones del paciente encontradas:', remisiones.length);
      return { success: true, data: remisiones };
    } catch (error) {
      console.error('❌ REMISION SERVICE: Error buscando remisiones del paciente:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar remisión
  async updateRemision(remisionId, updateData) {
    try {
      console.log('🏥 REMISION SERVICE: Actualizando remisión:', remisionId);
      const docRef = doc(db, 'remisiones', remisionId);
      await updateDoc(docRef, {
        ...updateData,
        fechaActualizacion: serverTimestamp()
      });

      console.log('✅ REMISION SERVICE: Remisión actualizada exitosamente');
      return { success: true };
    } catch (error) {
      console.error('❌ REMISION SERVICE: Error actualizando remisión:', error);
      return { success: false, error: error.message };
    }
  },

  // Eliminar remisión (soft delete)
  async deleteRemision(remisionId) {
    try {
      console.log('🏥 REMISION SERVICE: Eliminando remisión:', remisionId);
      const docRef = doc(db, 'remisiones', remisionId);
      await updateDoc(docRef, {
        activo: false,
        fechaEliminacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp()
      });

      console.log('✅ REMISION SERVICE: Remisión eliminada exitosamente (soft delete)');
      return { success: true };
    } catch (error) {
      console.error('❌ REMISION SERVICE: Error eliminando remisión:', error);
      return { success: false, error: error.message };
    }
  }
};
